import React, { useEffect, useMemo, useState } from "react";
import BackIcon from "@mui/icons-material/ArrowBack";
import RequestIcon from "@mui/icons-material/AssignmentOutlined";
import DateIcon from "@mui/icons-material/CalendarMonthOutlined";
import CountryIcon from "@mui/icons-material/LanguageOutlined";
import LocationIcon from "@mui/icons-material/LocationOnOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Chip as UiChip } from "../../components/ui";
import WhatsAppMessageDialog from "../../components/common/WhatsAppMessageDialog";
import { showErrorToast, showSuccessToast } from "../../components/common/ShowToast";
import apiService from "../../service/Api";
import type { RootState } from "../../store/Index";
import { tokens } from "../../theme";
import type { ChecklistRequestStatus, DiasporaChecklistRequest } from "./DiasporaChecklistRequestsPage";

const STATUS_LABELS: Record<ChecklistRequestStatus, string> = {
  NEW: "New",
  CONTACTED: "Contacted",
  CLOSED: "Closed",
};

const STATUS_COLORS: Record<ChecklistRequestStatus, "info" | "warning" | "neutral"> = {
  NEW: "info",
  CONTACTED: "warning",
  CLOSED: "neutral",
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const DiasporaChecklistRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const stateRequest = (location.state as { request?: DiasporaChecklistRequest } | null)?.request;
  const [request, setRequest] = useState<DiasporaChecklistRequest | null>(stateRequest ?? null);
  const [loading, setLoading] = useState(!stateRequest);
  const [error, setError] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  useEffect(() => {
    // The list page already fetched this row and passed it via navigation
    // state, so skip the duplicate GET when it's available.
    if (stateRequest) return;
    let active = true;
    const loadRequest = async () => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const response = await apiService.get<DiasporaChecklistRequest>(
          `/api/bookings/diaspora-checklist-requests/${id}/`,
        );
        if (active) setRequest(response.data);
      } catch {
        if (active) setError("This checklist request could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadRequest();
    return () => { active = false; };
  }, [id, stateRequest]);

  const applicantInitials = useMemo(
    () => request?.full_name.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "?",
    [request?.full_name],
  );

  const handleStatusChange = async (status: ChecklistRequestStatus) => {
    if (!request || status === request.status) return;
    setSavingStatus(true);
    try {
      const response = await apiService.patch<DiasporaChecklistRequest>(
        `/api/bookings/diaspora-checklist-requests/${request.id}/`,
        { status },
      );
      setRequest(response.data);
      showSuccessToast(`Request marked ${STATUS_LABELS[status].toLowerCase()}.`);
    } catch {
      showErrorToast("Could not update the request status.");
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: "50vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  if (!request) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/diaspora-checklist-requests")} sx={{ textTransform: "none" }}>Back to checklist requests</Button>
        <Alert severity="warning" sx={{ mt: 2 }}>{error || "This checklist request could not be found."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate("/diaspora-checklist-requests")}
        sx={{ mb: 2, color: colors.primary[400], textTransform: "none", fontWeight: 600 }}
      >
        Checklist requests
      </Button>

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, mb: 2.5, borderRadius: "14px", boxShadow: 0, bgcolor: colors.primary[900] }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <Avatar src={request.contact_image || undefined} sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: `${colors.primary[400]}20`, color: colors.primary[400], fontWeight: 700, flexShrink: 0 }}>
              {applicantInitials}
            </Avatar>
            <Box minWidth={0}>
              <Typography variant="h5" fontWeight={700} color={colors.primary[100]}>{request.full_name}</Typography>
              {request.contact ? (
                <Button
                  size="small"
                  onClick={() => navigate(`/contacts/individual/view/${request.contact}`)}
                  sx={{ px: 0, minWidth: 0, mt: 0.25, fontWeight: 600, textTransform: "none", textDecoration: "underline", textUnderlineOffset: "3px", color: colors.primary[400] }}
                >
                  {request.whatsapp_number}
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">{request.whatsapp_number}</Typography>
              )}
              <Typography variant="body2" color="text.secondary">Diaspora funeral checklist request · #{request.id}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <UiChip label={STATUS_LABELS[request.status]} status={STATUS_COLORS[request.status]} />
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => setMessageOpen(true)}
              sx={{ borderRadius: "10px", textTransform: "none", boxShadow: 0, bgcolor: colors.primary[400], color: theme.palette.getContrastText(colors.primary[400]), "&:hover": { bgcolor: colors.primary[500], boxShadow: 0 } }}
            >
              Message on WhatsApp
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: "14px", boxShadow: 0, bgcolor: colors.primary[900] }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
              <RequestIcon sx={{ color: colors.primary[400] }} />
              <Typography variant="h6" fontWeight={700}>Request details</Typography>
            </Stack>
            <Grid container spacing={2.5}>
              <DetailItem label="WhatsApp number" value={request.whatsapp_number} />
              <DetailItem label="Based in" value={request.based_in} icon={<CountryIcon fontSize="small" />} />
              <DetailItem label="Service location in Zimbabwe" value={request.service_location} icon={<LocationIcon fontSize="small" />} />
              <DetailItem label="Requested on" value={formatDate(request.created_at)} icon={<DateIcon fontSize="small" />} />
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: "14px", boxShadow: 0, bgcolor: colors.primary[900] }}>
            <Typography variant="h6" fontWeight={700} mb={1}>Follow-up status</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Track the team’s response to this checklist request.</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={request.status}
                disabled={savingStatus}
                onChange={(event) => void handleStatusChange(event.target.value as ChecklistRequestStatus)}
                inputProps={{ "aria-label": "Request status" }}
              >
                <MenuItem value="NEW">New</MenuItem>
                <MenuItem value="CONTACTED">Contacted</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </Select>
            </FormControl>
            {savingStatus && <Chip size="small" label="Saving…" sx={{ mt: 1.5 }} />}
          </Paper>
        </Grid>
      </Grid>

      <WhatsAppMessageDialog
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        clientName={request.full_name}
        clientPhone={request.whatsapp_number}
        staffName={user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "Muzukuru"}
        topic={`your request for the diaspora funeral planning checklist for ${request.service_location}`}
        initialMessage={`Hi ${request.full_name.trim().split(/\s+/)[0]}, thanks for requesting the Muzukuru diaspora funeral planning checklist. Is there anything we can help you coordinate for the service near ${request.service_location}?`}
        templateStorageKey="whatsapp_diaspora_checklist_template"
      />
    </Box>
  );
};

const DetailItem = ({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
      {icon && <Box sx={{ display: "flex", color: "text.secondary" }}>{icon}</Box>}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
    <Typography variant="body1" fontWeight={600}>{value || "—"}</Typography>
  </Grid>
);

export default DiasporaChecklistRequestDetailPage;
